import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createOwnersClient } from './api';
import type { OwnerMutationPayload, OwnersFilter, OwnerSchainDataset } from './model';

export const ownersQueryKey = ['owners'] as const;
export const ownersListsQueryKey = [...ownersQueryKey, 'list'] as const;
export const ownersSummaryQueryKey = [...ownersQueryKey, 'summary'] as const;
export const ownersDetailsQueryKey = [...ownersQueryKey, 'detail'] as const;
export const demandPartnerOwnersLookupQueryKey = ['demand-partners', 'owners'] as const;

function useOwnersClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createOwnersClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useOwnersQuery(filter: OwnersFilter) {
  const client = useOwnersClient();
  return useQuery({
    queryKey: [...ownersListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useOwnersSummaryQuery() {
  const client = useOwnersClient();
  return useQuery({ queryKey: ownersSummaryQueryKey, queryFn: () => client.summary() });
}

export function useOwnerQuery(id: string) {
  const client = useOwnersClient();
  return useQuery({
    queryKey: [...ownersDetailsQueryKey, id],
    queryFn: () => client.get(id),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useOwnerMutations() {
  const client = useOwnersClient();
  const queryClient = useQueryClient();
  const invalidateListsSummaryAndLookup = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ownersListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: ownersSummaryQueryKey }),
      queryClient.invalidateQueries({
        queryKey: demandPartnerOwnersLookupQueryKey,
        refetchType: 'none',
      }),
    ]);

  return {
    create: useMutation({
      mutationFn: (payload: OwnerMutationPayload) => client.create(payload),
      onSuccess: invalidateListsSummaryAndLookup,
    }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: OwnerMutationPayload }) =>
        client.update(id, payload),
      onSuccess: invalidateListsSummaryAndLookup,
    }),
    refreshSellersJson: useMutation({
      mutationFn: (id: string) => client.refreshSellersJson(id),
      onSuccess: invalidateListsSummaryAndLookup,
    }),
    downloadSchainTemplate: useMutation({
      mutationFn: (dataset: OwnerSchainDataset) => client.downloadSchainTemplate(dataset),
    }),
    downloadSchain: useMutation({
      mutationFn: ({ id, dataset }: { id: string; dataset: OwnerSchainDataset }) =>
        client.downloadSchain(id, dataset),
    }),
    uploadSchain: useMutation({
      mutationFn: ({
        id,
        dataset,
        file,
      }: {
        id: string;
        dataset: OwnerSchainDataset;
        file: File;
      }) => client.uploadSchain(id, dataset, file),
      onSuccess: async (_result, { id }) => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [...ownersDetailsQueryKey, id] }),
          queryClient.invalidateQueries({ queryKey: ownersListsQueryKey }),
          queryClient.invalidateQueries({ queryKey: ownersSummaryQueryKey }),
        ]);
      },
    }),
  };
}
